import {
  Box, Grid, TextField, Typography, Button, Paper, FormControl, InputLabel,
  Select, MenuItem, Divider, IconButton, Switch, FormControlLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, useMediaQuery, useTheme
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import AddIcon from "@mui/icons-material/Add";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import React, { useEffect, useState } from "react";
import { BANK_ACCOUNTS, BankAccountItem } from "../../../constants/bankAccounts";
import { IRaffle } from "../../../store/interfaces/IRaffle";
import { banks, getIconFromBankId } from "../../../constants/banks";
import { useUpdateRaffleBankAccountsMutation } from "../../../store/api";
import { enqueueSnackbar } from "notistack";
import { useIsMobileView } from "../../../utils/responsive";

interface BankAccountsProps {
  raffle: IRaffle
}

export const BankAccounts = ({
  raffle
}: BankAccountsProps) => {
  const theme = useTheme();
  const isMobile = useIsMobileView();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const initialBankAccounts = BANK_ACCOUNTS.find(
    (account) => account.accountId === raffle.accountId
  )?.bankAccounts || [];

  const [bankAccounts, setBankAccounts] = useState<BankAccountItem[]>(
    raffle.bankAccounts?.length > 0 
      ? raffle.bankAccounts 
      : initialBankAccounts
  );
  const [openDialog, setOpenDialog] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [editedAccount, setEditedAccount] = useState<BankAccountItem | null>(null);
  const [updateRaffleBankAccount, { data: updateRaffleBankAccountResponse }] = useUpdateRaffleBankAccountsMutation();

  const handleEditOpen = (index: number) => {
    setEditIndex(index);
    setEditedAccount({ ...bankAccounts[index] });
    setOpenDialog(true);
  };

  const handleAddBankAccount = () => {
    // Obtener el primer banco disponible como valor por defecto
    const defaultBank = banks.length > 0 ? banks[0] : null;
    
    const newAccount: BankAccountItem = {
      bankId: defaultBank?.id as any,
      bank: defaultBank?.name || '',
      accountType: 'Cuenta Corriente',
      name: '',
      dni: '',
      accountNumber: '',
      phone: '',
      ticketPrice: '',
      ticketPriceAmount: 0,
      ticketCurrency: 'CLP',
      hide: false,
    };
    
    setEditIndex(null);
    setEditedAccount(newAccount);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditedAccount(null);
    setEditIndex(null);
  };

  useEffect(() => {
    if (updateRaffleBankAccountResponse) {
      enqueueSnackbar(`Se han actualizado las cuentas bancarias exitosamente`, {
        variant: "success",
        anchorOrigin: {
          horizontal: "right",
          vertical: "top"
        },
      })
    }
  }, [updateRaffleBankAccountResponse])

  const handleAccountChange = (field: keyof BankAccountItem, value: any) => {
    if (!editedAccount) return;
    
    if (field === 'ticketPriceAmount') {
      setEditedAccount({
        ...editedAccount,
        [field]: value
      });
    } else if (field === 'bankId') {
      const foundedBank = banks.find(bank => bank.id === value);
      if (foundedBank) {
        setEditedAccount({
          ...editedAccount,
          bankId: value,
          bank: foundedBank.name
        });
      }
    } else {
      setEditedAccount({
        ...editedAccount,
        [field]: value
      });
    }
  };

  const handleConversionChange = (field: 'dolarPrice' | 'dolarPriceCurrency', value: any) => {
    if (!editedAccount) return;
    
    const updatedConversion = editedAccount.conversion ?? { dolarPrice: 0, dolarPriceCurrency: '' };
    setEditedAccount({
      ...editedAccount,
      conversion: {
        ...updatedConversion,
        [field]: field === 'dolarPrice' ? Number.parseInt(value) || 0 : value
      }
    });
  };

  const handleToggleHide = (index: number) => {
    const updatedAccounts = [...bankAccounts];
    updatedAccounts[index] = {
      ...updatedAccounts[index],
      hide: !updatedAccounts[index].hide
    };
    setBankAccounts(updatedAccounts);
    saveBankAccounts(updatedAccounts);
  };

  const handleMoveAccount = (index: number, direction: 'up' | 'down') => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= bankAccounts.length) return;

    const updatedAccounts = [...bankAccounts];
    [updatedAccounts[index], updatedAccounts[targetIndex]] =
      [updatedAccounts[targetIndex], updatedAccounts[index]];

    setBankAccounts(updatedAccounts);
    saveBankAccounts(updatedAccounts);
  };

  const saveBankAccounts = (accountsToSave: BankAccountItem[]) => {
    const updatedAccountsAux = accountsToSave.map(updatedAccount => ({
      ...updatedAccount,
      ticketPriceAmount: Number(updatedAccount.ticketPriceAmount),
      conversion: updatedAccount.conversion ? {
        dolarPrice: Number(updatedAccount.conversion.dolarPrice),
        dolarPriceCurrency: updatedAccount.conversion.dolarPriceCurrency
      } : updatedAccount.conversion
    }));

    updateRaffleBankAccount({
      raffleId: raffle.id,
      bankAccounts: updatedAccountsAux
    });
  };

  const handleSaveDialog = () => {
    if (editedAccount) {
      let updatedAccounts: BankAccountItem[];
      
      if (editIndex !== null) {
        // Editar cuenta existente
        updatedAccounts = bankAccounts.map((acc, i) =>
          i === editIndex ? { ...editedAccount } : acc
        );
      } else {
        // Agregar nueva cuenta
        updatedAccounts = [...bankAccounts, editedAccount];
      }
      
      setBankAccounts(updatedAccounts);
      setTimeout(() => {
        saveBankAccounts(updatedAccounts);
      }, 500);
    }
    handleCloseDialog();
  };

  return (
    <Box p={{ xs: 1, sm: 2, md: 3 }}>
      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {bankAccounts.map((bankAccount, index) => {
          const bank = banks.find(b => b.id === bankAccount.bankId);
          const accountKey = `${bankAccount.bankId}-${bankAccount.accountNumber}-${index}`;
          
          return (
            <Grid item xs={12} key={accountKey}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: { xs: 2, sm: 3 }, 
                  borderRadius: 2,
                  opacity: bankAccount.hide ? 0.5 : 1,
                  border: bankAccount.hide ? '1px dashed #ccc' : '1px solid transparent'
                }}
              >
                {/* Header de la Cuenta */}
                <Box 
                  display="flex" 
                  alignItems={isMobile ? "flex-start" : "center"} 
                  gap={2} 
                  mb={3}
                  flexDirection={isMobile ? "column" : "row"}
                >
                  <Box display="flex" alignItems="center" gap={2} width="100%">
                    {bank && getIconFromBankId({
                      bankId: bank.id,
                      width: isMobile ? 32 : 40,
                      height: isMobile ? 32 : 40,
                    })}
                    <Box flex={1} minWidth={0}>
                      <Typography 
                        variant={isMobile ? "subtitle1" : "h6"} 
                        fontWeight="600"
                        sx={{ wordBreak: "break-word" }}
                      >
                        {bank?.name || bankAccount.bank || 'Nueva Cuenta Bancaria'}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          fontSize: isMobile ? "0.75rem" : "0.875rem",
                          wordBreak: "break-word"
                        }}
                      >
                        {bankAccount.accountType || 'Seleccione tipo'} • {bankAccount.accountNumber || 'Número de cuenta'}
                      </Typography>
                      {bankAccount.phone && (
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ fontSize: isMobile ? "0.75rem" : "0.875rem" }}
                        >
                          Tel: {bankAccount.phone}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    alignSelf={isMobile ? "flex-end" : "center"}
                    mt={isMobile ? -6 : 0}
                  >
                    <IconButton
                      color="primary"
                      onClick={() => handleMoveAccount(index, 'up')}
                      disabled={index === 0}
                      aria-label="subir cuenta"
                      title="Subir"
                      size={isMobile ? "small" : "medium"}
                    >
                      <ArrowUpwardIcon />
                    </IconButton>
                    <IconButton
                      color="primary"
                      onClick={() => handleMoveAccount(index, 'down')}
                      disabled={index === bankAccounts.length - 1}
                      aria-label="bajar cuenta"
                      title="Bajar"
                      size={isMobile ? "small" : "medium"}
                    >
                      <ArrowDownwardIcon />
                    </IconButton>
                    <IconButton
                      color={bankAccount.hide ? "default" : "primary"}
                      onClick={() => handleToggleHide(index)}
                      aria-label={bankAccount.hide ? "mostrar cuenta" : "ocultar cuenta"}
                      title={bankAccount.hide ? "Mostrar cuenta" : "Ocultar cuenta"}
                      size={isMobile ? "small" : "medium"}
                    >
                      {bankAccount.hide ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                    <IconButton
                      color="primary"
                      onClick={() => handleEditOpen(index)}
                      aria-label="editar cuenta bancaria"
                      size={isMobile ? "small" : "medium"}
                    >
                      <EditIcon />
                    </IconButton>
                  </Box>
                </Box>

                <Divider sx={{ mb: { xs: 2, sm: 3 } }} />

                {/* Información Resumida */}
                <Grid container spacing={{ xs: 1, sm: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ fontSize: isMobile ? "0.75rem" : "0.875rem" }}
                    >
                      <strong>Nombre:</strong> {bankAccount.name || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ fontSize: isMobile ? "0.75rem" : "0.875rem" }}
                    >
                      <strong>DNI:</strong> {bankAccount.dni || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ fontSize: isMobile ? "0.75rem" : "0.875rem" }}
                    >
                      <strong>Precio:</strong> {bankAccount.ticketPrice || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ fontSize: isMobile ? "0.75rem" : "0.875rem" }}
                    >
                      <strong>Monto:</strong> {bankAccount.ticketPriceAmount || 0} {bankAccount.ticketCurrency || ''}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          );
        })}

        {/* Botón para agregar cuenta bancaria */}
        <Grid item xs={12}>
          <Button
            variant="outlined"
            fullWidth
            size={isMobile ? "medium" : "large"}
            startIcon={<AddIcon />}
            onClick={handleAddBankAccount}
            sx={{
              py: { xs: 1.5, sm: 2 },
              borderStyle: 'dashed',
              borderWidth: 2,
              fontSize: { xs: "0.875rem", sm: "1rem" },
              '&:hover': {
                borderStyle: 'dashed',
                borderWidth: 2,
              }
            }}
          >
            Agregar Cuenta Bancaria
          </Button>
        </Grid>
      </Grid>

      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        fullScreen={fullScreen}
        sx={{
          '& .MuiDialog-paper': {
            m: { xs: 1, sm: 2 },
            maxHeight: { xs: '100%', sm: '90vh' }
          }
        }}
      >
        <DialogTitle sx={{ 
          fontSize: { xs: "1.1rem", sm: "1.25rem" },
          pb: { xs: 1, sm: 2 }
        }}>
          {editIndex === null ? 'Nueva Cuenta Bancaria' : 'Editar Cuenta Bancaria'}
        </DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 1, sm: 2 } }}>
          {editedAccount && (
            <Box>
              <Grid container spacing={{ xs: 2, sm: 3 }}>
                {/* Información del Banco */}
                <Grid item xs={12} md={6}>
                  <FormControl variant="outlined" fullWidth>
                    <InputLabel>Banco</InputLabel>
                    <Select
                      value={editedAccount.bankId || ""}
                      onChange={(e) => handleAccountChange('bankId', e.target.value)}
                      label="Banco"
                      disabled={editIndex !== null}
                    >
                      {banks.map((bank) => (
                        <MenuItem key={bank.id} value={bank.id}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {getIconFromBankId({
                              bankId: bank.id,
                              width: 20,
                              height: 20,
                            })}
                            <Typography>{bank.name}</Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl variant="outlined" fullWidth>
                    <InputLabel>Tipo de cuenta</InputLabel>
                    <Select
                      value={editedAccount.accountType || ""}
                      onChange={(e) => handleAccountChange('accountType', e.target.value)}
                      label="Tipo de cuenta"
                    >
                      {[
                        "Cuenta Corriente",
                        "Cuenta RUT",
                        "Cuenta Vista",
                        "Cuenta de Ahorro",
                        "Nequi",
                        "Zelle",
                        "Binance",
                        "Pago Móvil",
                      ].map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Información Personal */}
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Nombre del titular"
                    variant="outlined"
                    fullWidth
                    value={editedAccount.name || ""}
                    onChange={(e) => handleAccountChange('name', e.target.value)}
                    helperText="Nombre completo del titular"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="DNI / Cédula / RUT"
                    variant="outlined"
                    fullWidth
                    value={editedAccount.dni || ""}
                    onChange={(e) => handleAccountChange('dni', e.target.value)}
                    helperText="Documento de identidad"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="Número de cuenta"
                    variant="outlined"
                    fullWidth
                    value={editedAccount.accountNumber || ""}
                    onChange={(e) => handleAccountChange('accountNumber', e.target.value)}
                    helperText="Número de cuenta bancaria"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="Teléfono"
                    variant="outlined"
                    fullWidth
                    value={editedAccount.phone || ""}
                    onChange={(e) => handleAccountChange('phone', e.target.value)}
                    helperText="Número de teléfono (opcional)"
                  />
                </Grid>

                {/* Precios */}
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Precio (texto)"
                    variant="outlined"
                    fullWidth
                    value={editedAccount.ticketPrice || ""}
                    onChange={(e) => handleAccountChange('ticketPrice', e.target.value)}
                    helperText="Ejemplo: $10.000"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Precio (número)"
                    variant="outlined"
                    fullWidth
                    type="text"
                    value={editedAccount.ticketPriceAmount || ""}
                    onChange={(e) => handleAccountChange('ticketPriceAmount', e.target.value)}
                    helperText="Solo números, sin letras"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="Moneda"
                    variant="outlined"
                    fullWidth
                    value={editedAccount.ticketCurrency || ""}
                    onChange={(e) => handleAccountChange('ticketCurrency', e.target.value)}
                    helperText="Ejemplo: CLP, USD, EUR"
                  />
                </Grid>

                {/* Toggle Hide/Show */}
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!editedAccount.hide}
                        onChange={(e) => handleAccountChange('hide', !e.target.checked)}
                        color="primary"
                      />
                    }
                    label={editedAccount.hide ? "Cuenta oculta" : "Cuenta visible"}
                  />
                </Grid>

                {/* Campos de Conversión */}
                <Grid item xs={12} md={6}>
                  <Box display="flex" gap={1} alignItems="center" mb={1}>
                    <Typography variant="body2" color="text.secondary">
                      ¿Tiene conversión?
                    </Typography>
                    <Button
                      size="small"
                      variant={editedAccount.conversion ? "contained" : "outlined"}
                      onClick={() => {
                        if (editedAccount.conversion) {
                          handleAccountChange('conversion', undefined);
                        } else {
                          handleAccountChange('conversion', { dolarPrice: 0, dolarPriceCurrency: '' });
                        }
                      }}
                    >
                      {editedAccount.conversion ? "Sí" : "No"}
                    </Button>
                  </Box>
                </Grid>

                {editedAccount.conversion && (
                  <>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Precio del dólar"
                        variant="outlined"
                        fullWidth
                        type="number"
                        value={editedAccount.conversion.dolarPrice || ""}
                        onChange={(e) => handleConversionChange('dolarPrice', e.target.value)}
                        helperText="Precio de conversión"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Moneda del dólar"
                        variant="outlined"
                        fullWidth
                        value={editedAccount.conversion.dolarPriceCurrency || ""}
                        onChange={(e) => handleConversionChange('dolarPriceCurrency', e.target.value)}
                        helperText="Ejemplo: USD, EUR"
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          p: { xs: 1, sm: 2 },
          flexDirection: { xs: "column-reverse", sm: "row" },
          gap: { xs: 1, sm: 0 }
        }}>
          <Button 
            onClick={handleCloseDialog} 
            color="secondary"
            fullWidth={isMobile}
            size={isMobile ? "medium" : "large"}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSaveDialog} 
            color="primary" 
            variant="contained"
            fullWidth={isMobile}
            size={isMobile ? "medium" : "large"}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
